import { FC, useContext, useEffect, useRef, useState } from "react";
import NotificationDispatch, { showErrorNotification } from "../../context/notificationContext";
import { usePageContext } from "../../context/pageContext";
import { useDebounce } from "../../hooks/useDebounce";
import useListFilters from "../../hooks/useListFilters";
import useToggle from "../../hooks/useToggle";
import { useI18n } from "../../hooks/useTranslations";
import useSupplierApi from "../../http/supplier/api";
import { InsertSupplier, Supplier, UpdateSupplier } from "../../http/supplier/types";
import { PaginationQuery } from "../../types";
import SvgAdd from "../icons/Add";
import SvgDelete from "../icons/Delete";
import SvgEdit from "../icons/Edit";
import Button from "../ui/Button";
import { Action } from "../ui/ListItemMenu";
import ModalDialog from "../ui/ModalDialog";
import Sidebar from "../ui/Sidebar";
import Table from "../ui/Table";
import TH from "../ui/TH";
import SupplierForm, { SupplierFormBody } from "./SupplierForm";

const Suppliers: FC = () => {
  const { useSuppliers: getSuppliers } = useSupplierApi();
  const filters = useListFilters({ isSearchable: true });
  const debouncedValue = useDebounce(filters.state.q);
  const aborter = useRef(new AbortController());
  const { isOpen, open, close } = useToggle();
  const [editSupplier, setEditSupplier] = useState<Supplier>();
  const [deleteSupplier, setDeleteSupplier] = useState<Supplier>();
  const [isModalSubmitting, setIsModalSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const supplierApi = useSupplierApi();
  const i18n = useI18n();

  const pageContext = usePageContext();

  const query: PaginationQuery = { ...filters.state, q: debouncedValue };
  const { data, error } = getSuppliers(query);

  useEffect(() => {
    error && dispatch(showErrorNotification(i18n, error));
  }, [error]);

  useEffect(() => {
    pageContext.setPage({ title: i18n.t.suppliers.plural });
  }, [i18n.language]);

  const onSubmit = async (formData: SupplierFormBody) => {
    if (!editSupplier) {
      const insertData: InsertSupplier = {
        code: formData.code,
        name: formData.name,
      };

      try {
        await supplierApi.createSupplier(insertData);
        close();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    } else {
      const updateData: UpdateSupplier = {
        name: formData.name,
      };

      try {
        await supplierApi.updateSupplier(updateData, editSupplier.code);
        close();
      } catch (err) {
        dispatch(showErrorNotification(i18n, err));
      }
    }
  };

  const actions: Action<Supplier>[] = [
    {
      title: i18n.t.common.edit,
      onClick: (item) => {
        setEditSupplier(item);
        open();
      },
      icon: SvgEdit,
    },
    {
      title: i18n.t.common.delete,
      onClick: setDeleteSupplier,
      icon: SvgDelete,
      color: "red",
      isDivider: true,
    },
  ];

  const orderOptions = {
    by: filters.state.orderBy,
    desc: filters.state.desc,
    setBy: filters.setOrder,
  };

  return (
    <>
      <Sidebar
        isOpen={isOpen}
        title={!editSupplier ? i18n.t.suppliers.newSupplier : i18n.t.suppliers.editSupplier}
        onClose={close}
      >
        <SupplierForm
          onSubmit={onSubmit}
          supplier={editSupplier}
          key={isOpen ? editSupplier?.code ?? "new" : "closed"}
        />
      </Sidebar>
      <Table<Supplier>
        buttons={{
          main: (
            <Button
              buttonProps={{
                onClick: () => {
                  setEditSupplier(undefined);
                  open();
                },
              }}
              glyph={SvgAdd}
            />
          ),
        }}
        searchOptions={{
          onChange: () => {
            aborter.current.abort();
            aborter.current = new AbortController();
          },
        }}
        rowActions={actions}
        onRowClick={(item) => {
          setEditSupplier(item);
          open();
        }}
        filters={filters}
        head={
          <tr>
            <th align="left">{i18n.t.common.code}</th>
            <TH fieldName="suuplier.name" title={i18n.t.common.name} order={orderOptions} />
            <th></th>
          </tr>
        }
        renderRow={(item) => (
          <>
            <td>{item.code}</td>
            <td>{item.name}</td>
          </>
        )}
        items={data?.items}
        total={data?.total}
        error={error}
      />
      {!!deleteSupplier && (
        <ModalDialog
          onClose={() => setDeleteSupplier(undefined)}
          title={i18n.t.suppliers.delete}
          isSubmitting={isModalSubmitting}
          actions={[
            {
              title: i18n.t.common.cancel,
              type: "secondary",
              onClick: () => setDeleteSupplier(undefined),
            },
            {
              title: i18n.t.common.yes,
              onClick: async () => {
                setIsModalSubmitting(true);
                if (!deleteSupplier) return;
                try {
                  await supplierApi.deleteSupplier(deleteSupplier.code);
                  setIsModalSubmitting(false);
                  setDeleteSupplier(undefined);
                } catch (e) {
                  setIsModalSubmitting(false);
                  dispatch(showErrorNotification(i18n, e));
                }
              },
            },
          ]}
        >
          {i18n.t.suppliers.questions.delete}
        </ModalDialog>
      )}
    </>
  );
};

export default Suppliers;
